import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { Users, Rocket, Award, Clock, Target, Heart, Lightbulb, ChevronRight } from 'lucide-react';

const stats = [
  { icon: Users, value: '50+', label: 'Mutlu Müşteri' },
  { icon: Rocket, value: '100+', label: 'Tamamlanan Proje' },
  { icon: Award, value: '10+', label: 'Yıllık Deneyim' },
  { icon: Clock, value: '7/24', label: 'Destek Hizmeti' },
];

const values = [
  {
    icon: Target,
    title: 'Hedef Odaklı',
    description: 'Her projede müşteri hedeflerini önceliklendiriyor, sonuç odaklı çalışıyoruz.'
  },
  {
    icon: Heart,
    title: 'Tutkulu Ekip',
    description: 'İşimizi seviyoruz ve bu tutku her projemize yansıyor.'
  },
  {
    icon: Lightbulb,
    title: 'Yenilikçi Yaklaşım',
    description: 'En son teknolojileri takip ediyor ve projelerimize entegre ediyoruz.'
  },
];

// Takım Listesi: 'name' dosya adı ile, 'realName' ise ekranda görünecek isimle eşleşmeli
const team = [
  { name: 'can-gevez', role: 'Kurucu & CEO', initial: 'C', realName: 'Can Gevez' },
  { name: 'eren-senaydin', role: 'Co-Founder', initial: 'E', realName: 'Eren Şenaydın' },
  { name: 'davut-tekin', role: 'Lead Developer', initial: 'D', realName: 'Davut Tekin' },
  { name: 'dogukan-alioglu', role: 'UX Designer', initial: 'D', realName: 'Doğukan Alioğlu' },
];

const textVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.6,
      ease: 'easeOut',
    },
  }),
};

const FloatingShapes = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    <div 
      className="absolute w-32 h-32 rounded-full opacity-20 animate-float"
      style={{
        top: '10%',
        left: '5%',
        background: 'radial-gradient(circle, #00E5FF 0%, transparent 70%)',
        animationDelay: '0s',
      }}
    />
    <div 
      className="absolute w-0 h-0 opacity-30 animate-float"
      style={{
        top: '60%',
        right: '10%',
        borderLeft: '30px solid transparent',
        borderRight: '30px solid transparent',
        borderBottom: '50px solid #B026FF',
        animationDelay: '1s',
      }}
    />
    <div 
      className="absolute w-24 h-24 rounded-full border-2 border-brand-electric-blue/30 opacity-40 animate-float"
      style={{
        top: '30%',
        right: '20%',
        animationDelay: '0.5s',
      }}
    />
  </div>
);

function AboutPage() {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });

  return (
    <div className="pt-24" data-testid="about-page">
      <section className="py-16 md:py-24 bg-background-primary relative overflow-hidden">
        <FloatingShapes />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <p className="text-xs tracking-[0.3em] uppercase text-brand-cyan font-mono mb-4">
              Hakkımızda
            </p>
            <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Dijital <span className="gradient-text">Dönüşümün</span> Öncüsü
            </h1>
          </motion.div>
        </div>
      </section>

      <section ref={sectionRef} className="py-16 bg-background-secondary">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <motion.h2
                custom={1}
                initial="hidden"
                animate={isInView ? 'visible' : 'hidden'}
                variants={textVariants}
                className="font-display text-2xl md:text-3xl font-bold text-white mb-6"
              >
                Biz Kimiz?
              </motion.h2>

              <motion.p
                custom={2}
                initial="hidden"
                animate={isInView ? 'visible' : 'hidden'}
                variants={textVariants}
                className="font-mono text-text-secondary leading-relaxed mb-6"
              >
                GEVEZ SOFTWARE olarak, işletmelerin dijital dönüşüm yolculuğunda yanlarında yer alıyoruz. 
                Modern teknolojiler ve yenilikçi çözümlerle müşterilerimizin rekabet avantajı elde etmelerini sağlıyoruz.
              </motion.p>

              <motion.p
                custom={3}
                initial="hidden"
                animate={isInView ? 'visible' : 'hidden'}
                variants={textVariants}
                className="font-mono text-text-secondary leading-relaxed mb-8"
              >
                Uzman ekibimiz, her projeye özel yaklaşım geliştirerek 
                <span className="text-brand-cyan"> müşteri memnuniyetini </span> 
                en üst düzeyde tutmayı hedefler.
              </motion.p>

              <motion.div
                custom={4}
                initial="hidden"
                animate={isInView ? 'visible' : 'hidden'}
                variants={textVariants}
              >
                <Link
                  to="/iletisim"
                  className="inline-block px-8 py-4 border border-brand-cyan text-brand-cyan font-display font-bold text-sm uppercase tracking-wider
                             hover:bg-brand-cyan hover:text-background-primary hover:shadow-[0_0_30px_rgba(0,229,255,0.5)] transition-all duration-300"
                >
                  Bizi Tanıyın
                </Link>
              </motion.div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              {stats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <motion.div
                    key={index}
                    custom={index + 5}
                    initial="hidden"
                    animate={isInView ? 'visible' : 'hidden'}
                    variants={textVariants}
                    className="glass p-6 md:p-8 rounded-lg text-center group hover:border-brand-cyan/30 transition-all duration-300"
                  >
                    <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-lg bg-brand-cyan/10 group-hover:bg-brand-cyan/20 transition-colors">
                      <Icon className="w-6 h-6 text-brand-cyan" />
                    </div>
                    <h3 className="font-display text-3xl md:text-4xl font-bold text-white mb-2 group-hover:text-brand-cyan transition-colors">
                      {stat.value}
                    </h3>
                    <p className="font-mono text-sm text-text-secondary">
                      {stat.label}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-background-primary">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-xs tracking-[0.3em] uppercase text-brand-cyan font-mono mb-4">
              Değerlerimiz
            </p>
            <h2 className="font-display text-2xl md:text-3xl font-bold text-white">
              Bizi Biz Yapan <span className="gradient-text">Değerler</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="glass p-8 rounded-lg text-center group hover:border-brand-cyan/30 transition-all duration-300"
                >
                  <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center rounded-full bg-brand-cyan/10 group-hover:bg-brand-cyan/20 transition-colors">
                    <Icon className="w-8 h-8 text-brand-cyan" />
                  </div>
                  <h3 className="font-display text-xl font-bold text-white mb-4">{value.title}</h3>
                  <p className="font-mono text-sm text-text-secondary">{value.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-background-secondary">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-xs tracking-[0.3em] uppercase text-brand-cyan font-mono mb-4">
              Ekibimiz
            </p>
            <h2 className="font-display text-2xl md:text-3xl font-bold text-white">
              Uzman <span className="gradient-text">Kadromuz</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {team.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center group"
              >
                {/* Profil Fotoğrafı Alanı */}
                <div className="w-24 h-24 mx-auto mb-4 rounded-full overflow-hidden border border-white/10 group-hover:border-brand-cyan/30 transition-all duration-300">
                  <img 
                    src={`/assets/team/${member.name}.png`} 
                    alt={member.realName}
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500"
                    onError={(e) => { e.target.src = "https://ui-avatars.com/api/?name=" + member.initial + "&background=0D0E19&color=00E5FF" }} 
                  />
                </div>
                <h3 className="font-display text-white font-bold mb-1">{member.realName}</h3>
                <p className="font-mono text-xs text-text-muted">{member.role}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-background-primary">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-display text-2xl md:text-3xl font-bold text-white mb-6"
          >
            Birlikte <span className="gradient-text">Çalışalım</span>
          </motion.h2>
          <Link
            to="/iletisim"
            className="inline-flex items-center gap-2 px-8 py-4 bg-brand-cyan text-background-primary font-display font-bold text-sm uppercase tracking-wider
                       hover:bg-white hover:shadow-[0_0_30px_rgba(0,229,255,0.6)] transition-all duration-300"
          >
            İletişime Geç <ChevronRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}

export default AboutPage;